"use client"
import { useState } from "react"
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Chip,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  Alert,
} from "@mui/material"
import { Add, Edit, Delete, Warning, Inventory, TrendingDown } from "@mui/icons-material"
import { useApp } from "../contexts/AppContext"

export default function InventoryManagement({ user }) {
  const { state, actions } = useApp()

  const [open, setOpen] = useState(false)
  const [selectedItem, setSelectedItem] = useState(null)
  const [formData, setFormData] = useState({
    name: "",
    category: "",
    supplier: "",
    quantity: 0,
    minStock: 0,
    maxStock: 0,
    unitPrice: 0,
  })

  const categories = [
    "Medical Supplies",
    "Medical Equipment",
    "Pharmaceuticals",
    "Laboratory Supplies",
    "Office Supplies",
    "Cleaning Supplies",
  ]

  const handleOpen = (item = null) => {
    if (item) {
      setSelectedItem(item)
      setFormData({
        name: item.name,
        category: item.category,
        supplier: item.supplier,
        quantity: item.quantity,
        minStock: item.minStock,
        maxStock: item.maxStock,
        unitPrice: item.unitPrice,
      })
    } else {
      setSelectedItem(null)
      setFormData({
        name: "",
        category: "",
        supplier: "",
        quantity: 0,
        minStock: 0,
        maxStock: 0,
        unitPrice: 0,
      })
    }
    setOpen(true)
  }

  const handleClose = () => {
    setOpen(false)
    setSelectedItem(null)
  }

  const getStatus = (quantity, minStock) => {
    if (quantity === 0) return "Out of Stock"
    if (quantity <= minStock) return "Low Stock"
    return "In Stock"
  }

  const handleSave = () => {
    const totalValue = formData.quantity * formData.unitPrice
    const status = getStatus(formData.quantity, formData.minStock)

    if (selectedItem) {
      actions.updateInventoryItem({
        ...selectedItem,
        ...formData,
        totalValue,
        status,
        lastRestocked: new Date().toISOString().split("T")[0],
      })
    } else {
      actions.addInventoryItem({
        ...formData,
        totalValue,
        status,
        lastRestocked: new Date().toISOString().split("T")[0],
      })
    }
    handleClose()
  }

  const handleDelete = (id) => {
    actions.deleteInventoryItem(id)
  }

  const getStatusColor = (status) => {
    switch (status) {
      case "In Stock":
        return "success"
      case "Low Stock":
        return "warning"
      case "Out of Stock":
        return "error"
      default:
        return "default"
    }
  }

  const lowStockItems = state.inventory.filter((item) => item.status === "Low Stock")
  const outOfStockItems = state.inventory.filter((item) => item.status === "Out of Stock")
  const totalValue = state.inventory.reduce((sum, item) => sum + item.totalValue, 0)
  const totalItems = state.inventory.length

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Inventory Management</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={() => handleOpen()}>
          Add New Item
        </Button>
      </Box>

      {/* Alerts */}
      {(lowStockItems.length > 0 || outOfStockItems.length > 0) && (
        <Box mb={3}>
          {outOfStockItems.length > 0 && (
            <Alert severity="error" sx={{ mb: 1 }}>
              <Typography variant="body2">
                {outOfStockItems.length} item(s) are out of stock: {outOfStockItems.map((item) => item.name).join(", ")}
              </Typography>
            </Alert>
          )}
          {lowStockItems.length > 0 && (
            <Alert severity="warning">
              <Typography variant="body2">
                {lowStockItems.length} item(s) are running low: {lowStockItems.map((item) => item.name).join(", ")}
              </Typography>
            </Alert>
          )}
        </Box>
      )}

      {/* Summary Cards */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <Inventory sx={{ fontSize: 40, color: "primary.main", mr: 2 }} />
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Total Items
                  </Typography>
                  <Typography variant="h4">{totalItems}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <Warning sx={{ fontSize: 40, color: "warning.main", mr: 2 }} />
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Low Stock
                  </Typography>
                  <Typography variant="h4" color="warning.main">
                    {lowStockItems.length}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <TrendingDown sx={{ fontSize: 40, color: "error.main", mr: 2 }} />
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Out of Stock
                  </Typography>
                  <Typography variant="h4" color="error.main">
                    {outOfStockItems.length}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Inventory Value
              </Typography>
              <Typography variant="h4" color="success.main">
                ₹{totalValue.toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Item Name</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Supplier</TableCell>
              <TableCell>Quantity</TableCell>
              <TableCell>Min Stock</TableCell>
              <TableCell>Unit Price</TableCell>
              <TableCell>Total Value</TableCell>
              <TableCell>Last Restocked</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {state.inventory.map((item) => (
              <TableRow key={item.id}>
                <TableCell>{item.name}</TableCell>
                <TableCell>{item.category}</TableCell>
                <TableCell>{item.supplier}</TableCell>
                <TableCell>{item.quantity}</TableCell>
                <TableCell>{item.minStock}</TableCell>
                <TableCell>₹{item.unitPrice}</TableCell>
                <TableCell>₹{item.totalValue.toLocaleString()}</TableCell>
                <TableCell>{item.lastRestocked}</TableCell>
                <TableCell>
                  <Chip label={item.status} color={getStatusColor(item.status)} size="small" />
                </TableCell>
                <TableCell>
                  <Tooltip title="Edit">
                    <IconButton size="small" onClick={() => handleOpen(item)}>
                      <Edit />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton size="small" onClick={() => handleDelete(item.id)}>
                      <Delete />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add/Edit Item Dialog */}
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>{selectedItem ? "Edit Inventory Item" : "Add New Inventory Item"}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Item Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Category"
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                SelectProps={{ native: true }}
              >
                <option value="">Select Category</option>
                {categories.map((category) => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Supplier"
                value={formData.supplier}
                onChange={(e) => setFormData({ ...formData, supplier: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Unit Price (₹)"
                type="number"
                value={formData.unitPrice}
                onChange={(e) => setFormData({ ...formData, unitPrice: Number.parseFloat(e.target.value) })}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Current Quantity"
                type="number"
                value={formData.quantity}
                onChange={(e) => setFormData({ ...formData, quantity: Number.parseInt(e.target.value) })}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Minimum Stock Level"
                type="number"
                value={formData.minStock}
                onChange={(e) => setFormData({ ...formData, minStock: Number.parseInt(e.target.value) })}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Maximum Stock Level"
                type="number"
                value={formData.maxStock}
                onChange={(e) => setFormData({ ...formData, maxStock: Number.parseInt(e.target.value) })}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2" color="textSecondary">
                Total Value: ₹{(formData.quantity * formData.unitPrice).toLocaleString()}
              </Typography>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">
            {selectedItem ? "Update" : "Add"} Item
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
